import { useEffect, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { ChevronLeft, ChevronRight, Clock, Video } from 'lucide-react'

type MeetingType = { id: string; name: string; duration: number; description: string }
type ScreenInfo = {
  headline: string
  intro: string
  hostName: string
  businessName: string
  logoUrl: string | null
  accent: string
  background: string
  returnLabel: string
  returnUrl: string
  timezone: string
  windowDays: number
  openDays: string[]
  types: MeetingType[]
}
type Slot = { start: string; label: string }

export default function MeetBook() {
  const { businessSlug, screenSlug } = useParams<{ businessSlug: string; screenSlug: string }>()
  const [screen, setScreen] = useState<ScreenInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [typeId, setTypeId] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [availableDays, setAvailableDays] = useState<string[] | null>(null)
  const [date, setDate] = useState<string | null>(null)
  const [slots, setSlots] = useState<Slot[]>([])
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [picked, setPicked] = useState<Slot | null>(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [booking, setBooking] = useState(false)
  const [booked, setBooked] = useState<{ meetLink: string | null; start: string; end: string; summary: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    callMeeting({ action: 'screen', businessSlug, screenSlug }).then((data) => {
      if (cancelled) return
      if (data?.error || !data?.screen) {
        setError(data?.error || 'This booking page is not available.')
        setScreen(null)
      } else {
        setScreen(data.screen)
        setTypeId(data.screen.types?.[0]?.id || null)
      }
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [businessSlug, screenSlug])

  const days = availableDays || []
  const visibleDays = days.slice(page * 5, page * 5 + 5)
  const selectedType = screen?.types.find((type) => type.id === typeId) || null

  useEffect(() => {
    if (!screen || !typeId) return
    let cancelled = false
    setAvailableDays(null)
    setDate(null)
    setPage(0)
    setSlots([])
    callMeeting({ action: 'days', businessSlug, screenSlug, meetingTypeId: typeId }).then((data) => {
      if (cancelled) return
      if (data?.error) {
        setAvailableDays([])
        setError(data.error)
      } else {
        setError(null)
        setAvailableDays(Array.isArray(data?.dates) ? data.dates : [])
      }
    })
    return () => { cancelled = true }
  }, [screen, typeId, businessSlug, screenSlug])

  useEffect(() => {
    if (availableDays === null) return
    if (!availableDays.length) {
      setDate(null)
      return
    }
    if (page > 0 && page * 5 >= availableDays.length) {
      setPage(Math.max(0, Math.ceil(availableDays.length / 5) - 1))
      return
    }
    if (!date || !availableDays.includes(date)) setDate(availableDays[page * 5] || availableDays[0])
  }, [availableDays, date, page])

  useEffect(() => {
    if (!screen || !date || !typeId) return
    let cancelled = false
    setSlotsLoading(true)
    setPicked(null)
    callMeeting({ action: 'slots', businessSlug, screenSlug, date, meetingTypeId: typeId }).then((data) => {
      if (cancelled) return
      if (data?.error) {
        setSlots([])
        setError(data.error)
      } else {
        const next = data?.slots || []
        setError(null)
        setSlots(next)
        if (!next.length) {
          setAvailableDays((current) => (current || []).filter((day) => day !== date))
          setDate(null)
        }
      }
      setSlotsLoading(false)
    })
    return () => { cancelled = true }
  }, [screen, date, typeId, businessSlug, screenSlug])

  const book = async () => {
    if (!picked || !typeId) return
    setBooking(true)
    const data = await callMeeting({
      action: 'book',
      businessSlug,
      screenSlug,
      meetingTypeId: typeId,
      start: picked.start,
      name,
      email,
      note,
    })
    setBooking(false)
    if (data?.error) {
      setError(data.error)
      return
    }
    setBooked(data)
  }

  if (loading) {
    return <Shell><p className="text-sm text-gray-500">Loading…</p></Shell>
  }
  if (!screen) {
    return <Shell><p className="text-sm text-gray-600">{error || 'This booking page is not available.'}</p></Shell>
  }

  const accent = screen.accent
  const background = screen.background || `${accent}14`

  if (booked) {
    const when = new Intl.DateTimeFormat('en-US', {
      timeZone: screen.timezone,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(booked.start))
    return (
      <Shell accent={accent} background={background}>
        <Card>
          {screen.logoUrl && <img src={screen.logoUrl} alt="" className="h-10 max-w-[160px] object-contain object-left mb-4" />}
          <h1 className="text-2xl font-semibold text-gray-900">You’re booked</h1>
          <p className="text-gray-600 mt-2">{booked.summary}</p>
          <p className="text-gray-800 font-medium mt-4">{when}</p>
          <p className="text-sm text-gray-500 mt-1">A calendar invite is on its way to {email}.</p>
          {(booked.meetLink || screen.returnUrl) && (
            <div className="mt-5 flex flex-wrap gap-2">
              {booked.meetLink && (
                <a href={booked.meetLink} className="inline-flex px-4 py-2.5 rounded-lg text-white text-sm font-semibold" style={{ background: accent }}>
                  Open Google Meet
                </a>
              )}
              {screen.returnUrl && (
                <a href={screen.returnUrl} className="inline-flex px-4 py-2.5 rounded-lg text-sm font-semibold border" style={{ borderColor: accent, color: accent }}>
                  {screen.returnLabel || 'Continue'}
                </a>
              )}
            </div>
          )}
        </Card>
      </Shell>
    )
  }

  return (
    <Shell accent={accent} background={background}>
      <div className="w-full max-w-4xl grid md:grid-cols-[280px_1fr] bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <aside className="p-6 border-b md:border-b-0 md:border-r border-gray-100">
          {screen.logoUrl && <img src={screen.logoUrl} alt="" className="h-10 max-w-[160px] object-contain object-left mb-4" />}
          <p className="text-sm text-gray-500">{screen.hostName}</p>
          <h1 className="text-xl font-semibold text-gray-900 mt-1">{screen.headline}</h1>
          {screen.intro && <p className="text-sm text-gray-600 mt-3 leading-relaxed">{screen.intro}</p>}
          <div className="mt-5 space-y-2">
            {screen.types.map((type) => {
              const selected = type.id === typeId
              return (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => { setTypeId(type.id); setPicked(null) }}
                  className="w-full text-left rounded-xl border px-3 py-2.5"
                  style={selected ? { borderColor: accent, background: `${accent}12` } : { borderColor: '#e5e7eb' }}
                >
                  <p className="text-sm font-medium text-gray-900">{type.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                    <Clock className="h-3 w-3" /> {type.duration} min
                    <Video className="h-3 w-3" /> Google Meet
                  </p>
                  {type.description && <p className="text-xs text-gray-500 mt-1">{type.description}</p>}
                </button>
              )
            })}
          </div>
        </aside>
        <section className="p-6">
          {picked && selectedType ? (
            <form
              onSubmit={(e) => { e.preventDefault(); void book() }}
              className="max-w-md"
            >
              <button type="button" onClick={() => setPicked(null)} className="text-sm font-medium mb-4" style={{ color: accent }}>
                Choose a different time
              </button>
              <h2 className="text-lg font-semibold text-gray-900">{selectedType.name}</h2>
              <p className="text-sm text-gray-600 mt-1">
                {new Intl.DateTimeFormat('en-US', {
                  timeZone: screen.timezone,
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                }).format(new Date(picked.start))}
                {' · '}{selectedType.duration} min · Google Meet
              </p>
              <label className="block mt-5 text-xs font-medium text-gray-600">Name
                <input required value={name} onChange={(e) => setName(e.target.value)} className="meet-input mt-1 w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" />
              </label>
              <label className="block mt-3 text-xs font-medium text-gray-600">Email
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="meet-input mt-1 w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" />
              </label>
              <label className="block mt-3 text-xs font-medium text-gray-600">Note
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="meet-input mt-1 w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" />
              </label>
              {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
              <button
                type="submit"
                disabled={booking}
                className="mt-5 w-full py-3 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                style={{ background: accent }}
              >
                {booking ? 'Booking…' : 'Confirm meeting'}
              </button>
            </form>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Pick a day</h2>
                  {visibleDays.length > 0 && (
                    <p className="text-xs text-gray-500">{monthHeading(visibleDays)}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <button type="button" disabled={page === 0} onClick={() => setPage((n) => Math.max(0, n - 1))} className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-30">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button type="button" disabled={(page + 1) * 5 >= days.length} onClick={() => setPage((n) => n + 1)} className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-30">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {availableDays === null ? (
                <p className="text-sm text-gray-500">Checking the calendar…</p>
              ) : days.length === 0 ? (
                <p className="text-sm text-gray-500">{error || 'No open times.'}</p>
              ) : (
              <div className="grid grid-cols-5 gap-2">
                {visibleDays.map((day) => {
                  const selected = day === date
                  const label = new Date(`${day}T12:00:00Z`)
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => setDate(day)}
                      className="rounded-xl border px-1 py-2 text-center"
                      style={selected ? { borderColor: accent, background: accent, color: 'white' } : { borderColor: '#e5e7eb' }}
                    >
                      <p className="text-[10px] uppercase tracking-wide opacity-80">{label.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })}</p>
                      <p className="text-[10px] uppercase tracking-wide opacity-70">{label.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })}</p>
                      <p className="text-sm font-semibold">{label.getUTCDate()}</p>
                    </button>
                  )
                })}
              </div>
              )}
              {days.length > 0 && (
              <div className="mt-5">
                {slotsLoading || !date ? (
                  <p className="text-sm text-gray-500">Checking the calendar…</p>
                ) : slots.length === 0 ? null : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {slots.map((slot) => (
                      <button
                        key={slot.start}
                        type="button"
                        onClick={() => { setPicked(slot); setError(null) }}
                        className="py-2.5 rounded-lg border text-sm font-medium hover:text-white"
                        style={{ borderColor: accent, color: accent }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = accent; e.currentTarget.style.color = 'white' }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = accent }}
                      >
                        {slot.label}
                      </button>
                    ))}
                  </div>
                )}
                {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
              </div>
              )}
            </>
          )}
        </section>
      </div>
    </Shell>
  )
}

async function callMeeting(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke('meeting-book', { body })
  if (!error) return data
  try {
    const response = (error as { context?: Response }).context
    if (response && typeof response.json === 'function') {
      const payload = await response.json()
      if (payload && typeof payload === 'object') return payload
    }
  } catch { /* body already consumed or not json */ }
  return { error: 'Something went wrong. Try again.' }
}

function monthHeading(days: string[]) {
  const part = (iso: string, withYear: boolean) =>
    new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', {
      month: 'long',
      ...(withYear ? { year: 'numeric' as const } : {}),
      timeZone: 'UTC',
    })
  const first = new Date(`${days[0]}T12:00:00Z`)
  const last = new Date(`${days[days.length - 1]}T12:00:00Z`)
  if (first.getUTCFullYear() === last.getUTCFullYear() && first.getUTCMonth() === last.getUTCMonth()) {
    return part(days[0], true)
  }
  if (first.getUTCFullYear() === last.getUTCFullYear()) {
    return `${part(days[0], false)} – ${part(days[days.length - 1], true)}`
  }
  return `${part(days[0], true)} – ${part(days[days.length - 1], true)}`
}

function Shell({ children, accent, background }: { children: ReactNode; accent?: string; background?: string }) {
  const page = background || (accent ? `${accent}14` : '#f3f4f6')
  const safeAccent = accent && /^#[0-9a-fA-F]{6}$/.test(accent) ? accent : ''
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: page }}>
      {safeAccent && (
        <style>{`.meet-input:focus{outline:none;border-color:${safeAccent};box-shadow:0 0 0 3px ${safeAccent}33;}`}</style>
      )}
      {children}
    </div>
  )
}

function Card({ children }: { children: ReactNode }) {
  return <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-200 p-6">{children}</div>
}
